import React, { useEffect } from 'react';

// For a real project, it's best to place these styles in a global CSS file (e.g., index.css)
// or use a CSS-in-JS solution. This <style> tag is included here for self-containment.
const GlobalStyles = () => (
  <style>{`
    body {
      font-family: 'Inter', sans-serif;
      background-color: #0a0a0a;
      color: #e2e8f0;
    }
    .gradient-text {
      background: linear-gradient(to right, #2dd4bf, #38bdf8);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .section {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 4rem 1.5rem;
      border-bottom: 1px solid #1e293b;
    }
    .card {
      background-color: rgba(30, 41, 59, 0.5);
      border: 1px solid #334155;
      border-radius: 1rem;
      padding: 2rem;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .card:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    }
    .nav-link {
      transition: color 0.3s;
    }
    .nav-link:hover {
      color: #2dd4bf;
    }
    /* Scroll Animation */
    .reveal {
      position: relative;
      transform: translateY(100px);
      opacity: 0;
      transition: 1s all ease;
    }
    .reveal.active {
      transform: translateY(0);
      opacity: 1;
    }
  `}</style>
);

const Pitch = (onBack: any) => {
  // The scroll animation logic is moved into a useEffect hook.
  // This is the standard React way to handle side effects like event listeners.
  useEffect(() => {
    function reveal() {
      const reveals = document.querySelectorAll(".reveal");
      for (let i = 0; i < reveals.length; i++) {
        const windowHeight = window.innerHeight;
        const elementTop = reveals[i].getBoundingClientRect().top;
        const elementVisible = 150;
        if (elementTop < windowHeight - elementVisible) {
          reveals[i].classList.add("active");
        } else {
          reveals[i].classList.remove("active");
        }
      }
    }

    window.addEventListener("scroll", reveal);
    // Initial check on load
    reveal();

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", reveal);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return (
    <>
      <GlobalStyles />
      {/* For fonts and Tailwind, add the <link> and <script> tags to your project's public/index.html */}
      

      <main className='bg-slate-900/50'>
        {/* Hero Section */}
        <section id="hero" className="section text-center relative overflow-hidden bg-slate-900/50">
          <div className="absolute inset-0 bg-grid-slate-900/50 [mask-image:linear-gradient(to_bottom,white_20%,transparent_75%)]"></div>
          <div className="relative z-10">
            <img 
              src="./logo.png" 
              alt="Enantio Logo" 
              className="h-32 w-32 mx-auto mb-8 rounded-full shadow-teal-500/20" 
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src='https://placehold.co/128x128/1e293b/ffffff?text=E'; }}
            />
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4 text-white">The Future of AI isn't Bigger.</h1>
            <h2 className="text-5xl md:text-7xl font-extrabold tracking-tight gradient-text">It's Resilient.</h2>
            <p className="mt-8 max-w-3xl mx-auto text-lg md:text-xl text-slate-400">
              Current AI is powerful but brittle, a one-trick pony. We're building the first Cognitive Architecture for true adaptation, common-sense, and resilience. This is the paradigm shift.
            </p>
            <div className="mt-10">
              <a href="#problem" className="bg-teal-500 text-black font-bold py-3 px-8 rounded-full text-lg hover:bg-teal-400 transition-all duration-300">Discover How</a>
            </div>
          </div>
        </section>

        {/* The Problem Section */}
        <section id="problem" className="section reveal bg-slate-900/50">
          <div className="container mx-auto text-center">
            <h2 className="text-sm font-bold uppercase tracking-widest text-teal-400 mb-4">The AI Scaling Wall</h2>
            <p className="text-4xl md:text-5xl font-bold text-white mb-12">Why Bigger Models Aren't Enough</p>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="card text-left">
                <h3 className="text-2xl font-bold mb-4 text-white">Static & Brittle</h3>
                <p className="text-slate-300">
                  Today's AI operates as a monolithic, static function. It excels at specific tasks but shatters when faced with ambiguity or novel contexts. It's a calculator that can't figure out how to open a door.
                </p>
              </div>
              <div className="card text-left">
                <h3 className="text-2xl font-bold mb-4 text-white">No Common Sense</h3>
                <p className="text-slate-300">
                  Despite their vast knowledge, LLMs lack robust, human-like common-sense reasoning. This leads to logical gaps and an inability to transfer learning effectively, hitting a wall on the path to AGI.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Solution Section */}
        <section id="solution" className="section reveal bg-slate-900/50">
          <div className="container mx-auto text-center">
            <h2 className="text-sm font-bold uppercase tracking-widest text-teal-400 mb-4">The Solution</h2>
            <p className="text-4xl md:text-5xl font-bold text-white mb-6">Introducing Enantio</p>
            <p className="max-w-3xl mx-auto text-lg text-slate-400 mb-12">
              Enantio is a neurologically-grounded cognitive architecture that enables AI to think, not just process. It's a dynamic, multi-agent system that adapts its cognitive strategy on the fly.
            </p>
            <div className="card max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-6 text-white">The 64-State Cognitive Hypercube</h3>
              <p className="text-slate-300 mb-6">
                The Enantio architecture is defined by 8 core cognitive functions operating as distinct agents. Their interplay creates a 64-state "meta-state hypercube," allowing the system to shift between processing modes, from deep analysis to creative exploration, to match any situation.
              </p>
              {/* Used the placeholder directly as the local file isn't available */}
              <img src="./meta-hypercube.png" alt="Hypercube Diagram" className="mx-auto rounded-lg" />
              <p className="text-xs mt-4 text-slate-500">Visualization of the TRPI Meta-State Hypercube, representing the system's cognitive flexibility.</p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how" className="section reveal bg-slate-900/50">
          <div className="container mx-auto">
            <h2 className="text-center text-sm font-bold uppercase tracking-widest text-teal-400 mb-4">The Secret Sauce</h2>
            <p className="text-center text-4xl md:text-5xl font-bold text-white mb-12">A Generative Grammar for Intelligence</p>
            <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
              <div className="card">
                <h3 className="text-2xl font-bold mb-4 text-white">1. Dynamic Agent System</h3>
                <p className="text-slate-300">
                  Instead of one model, Enantio uses specialized agents for different cognitive functions (e.g., logical deduction, pattern recognition, sensory processing). These agents combine into "chains of thought" to solve complex problems.
                </p>
              </div>
              <div className="card">
                <h3 className="text-2xl font-bold mb-4 text-white">2. Self-Verifying Schemas</h3>
                <p className="text-slate-300">
                  As Enantio interacts with the world, it builds a library of robust, verified "schemas" (mental models). This allows it to solve familiar problems instantly, slashing dependency on expensive foundational models and dramatically cutting computational costs.
                </p>
              </div>
              <div className="card">
                <h3 className="text-2xl font-bold mb-4 text-white">3. The Unfair Advantage</h3>
                <p className="text-slate-300">
                  We're not building a new LLM from scratch. Enantio is a <strong>supervising control layer</strong> that orchestrates existing foundational models. This capital-efficient approach leverages billions in existing R&D to create something entirely new.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Vision Section */}
        <section id="vision" className="section reveal bg-slate-900/50">
          <div className="container mx-auto text-center">
            <h2 className="text-sm font-bold uppercase tracking-widest text-teal-400 mb-4">Our Vision</h2>
            <p className="text-4xl md:text-5xl font-bold text-white mb-6">The Seed of AGI</p>
            <p className="max-w-3xl mx-auto text-lg text-slate-400 mb-12">
              Enantio is more than a product; it's a generative grammar for building resilient, psychologically plausible, and truly general AI. We are creating the foundational platform for the next generation of intelligent systems.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              <div className="card items-center text-center">
                <p className="text-4xl mb-2">🤖</p>
                <h3 className="text-xl font-bold text-white">Adaptive Robotics</h3>
              </div>
              <div className="card items-center text-center">
                <p className="text-4xl mb-2">🧠</p>
                <h3 className="text-xl font-bold text-white">Computational Psychiatry</h3>
              </div>
              <div className="card items-center text-center">
                <p className="text-4xl mb-2">🌐</p>
                <h3 className="text-xl font-bold text-white">Complex Systems Analysis</h3>
              </div>
              <div className="card items-center text-center">
                <p className="text-4xl mb-2">🚀</p>
                <h3 className="text-xl font-bold text-white">Autonomous Agents</h3>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="section reveal bg-slate-900/50">
          <div className="container mx-auto text-center">
            <img 
              src="./logo.png" 
              alt="Enantio Logo" 
              className="h-20 w-20 mx-auto mb-6 rounded-full" 
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src='https://placehold.co/80x80/1e293b/ffffff?text=E'; }}
            />
            <h2 className="text-4xl font-bold text-white mb-4">Let's Build the Future of Intelligence.</h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">We are currently raising our seed round to build out the core platform and secure first-mover advantage. We invite you to join us.</p>
            <a href="mailto:info@traitindicator.com" className="bg-gradient-to-r from-teal-500 to-blue-500 text-white font-bold py-4 px-10 rounded-full text-xl hover:opacity-90 transition-opacity duration-300">
              Contact Us
            </a>
          </div>
        </section>
      </main>
      
      <footer className="text-center py-6 border-t border-slate-800">
        <div className="mb-2 space-x-4">
          <a href="#/privacy" className="text-slate-500 hover:text-teal-400">Privacy Policy</a>
          <a href="#/terms" className="text-slate-500 hover:text-teal-400">Terms of Service</a>
          <a href="#/cookies" className="text-slate-500 hover:text-teal-400">Cookie Policy</a>
          <a href="#/contact" className="text-slate-500 hover:text-teal-400">Contact</a>
        </div>
        <p className="text-slate-500">&copy; 2025 Trait Indicator. All Rights Reserved.</p>
      </footer>
    </>
  );
};

export default Pitch;